import React from 'react';
import { useFramerieSEO } from '../hooks/useSEO';
import { seoConfig } from '../utils/seo-config';

interface SEOHeadProps {
  page?: keyof typeof seoConfig.pages;
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  noIndex?: boolean;
}

/**
 * Composant SEO Head pour Framerie
 * Gère automatiquement les méta-tags pour le référencement
 * Développé pour Dreambel Tech par Guénolé KIKABOU KIA MOUKALA
 * 
 * @param props Configuration SEO pour la page
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
  page = 'home',
  title,
  description,
  keywords,
  image,
  noIndex = false
}) => {
  // Utilisation du hook SEO spécialisé pour Framerie
  useFramerieSEO(page, {
    title,
    description,
    keywords,
    image,
    noIndex
  });

  return null; // Ce composant ne rend rien visuellement
};

/**
 * Composant pour injecter les méta-tags SEO de base
 * À utiliser dans le layout principal
 */
export const BaseSEOTags: React.FC = () => {
  React.useEffect(() => {
    // Ajout des méta-tags de base pour Framerie
    const metaTags = [
      { name: 'application-name', content: 'Framerie' },
      { name: 'apple-mobile-web-app-title', content: 'Framerie' },
      { name: 'theme-color', content: '#2563eb' },
      { name: 'msapplication-TileColor', content: '#2563eb' },
      { name: 'mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
      
      // Méta-tags pour l'icône dans les partages
      { name: 'msapplication-TileImage', content: '/assets/icons/ms-icon-144x144.png' },
      { name: 'msapplication-square70x70logo', content: '/assets/icons/ms-icon-70x70.png' },
      { name: 'msapplication-square150x150logo', content: '/assets/icons/ms-icon-150x150.png' },
      { name: 'msapplication-wide310x150logo', content: '/assets/icons/ms-icon-310x310.png' },
      { name: 'msapplication-square310x310logo', content: '/assets/icons/ms-icon-310x310.png' },
      
      // Méta-tags spécifiques au développeur
      { name: 'developer', content: 'Guénolé KIKABOU KIA MOUKALA' },
      { name: 'company', content: 'Dreambel Tech' },
      { name: 'build-tool', content: 'Vite + React + TypeScript' },
      { name: 'framework', content: 'React 19' },
      
      // Méta-tags pour les outils de développement
      { name: 'generator', content: 'Framerie Landing Page by Dreambel Tech' },
      { name: 'subject', content: 'Application de capture d\'écran moderne' },
      { name: 'classification', content: 'Productivity Software, Developer Tools' },
      { name: 'category', content: 'Screen Capture Application' },
      
      // Géolocalisation (si applicable)
      { name: 'geo.region', content: 'FR' },
      { name: 'geo.placename', content: 'France' },
      
      // Performance et cache
      { name: 'revisit-after', content: '7 days' },
      { name: 'distribution', content: 'global' },
      { name: 'rating', content: 'general' }
    ];

    metaTags.forEach(({ name, content }) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = name;
        document.head.appendChild(metaTag);
      }
      metaTag.content = content;
    });

    // Ajout des liens de préconnexion pour améliorer les performances
    const preconnectUrls = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://api.framerie.com' // API future si applicable
    ];

    preconnectUrls.forEach(url => {
      if (!document.querySelector(`link[href="${url}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = url;
        if (url.includes('gstatic')) {
          link.crossOrigin = 'anonymous';
        }
        document.head.appendChild(link);
      }
    });

    // Ajout des icônes Apple et favicon pour améliorer les partages
    const iconLinks = [
      { rel: 'apple-touch-icon', sizes: '57x57', href: '/assets/icons/apple-icon-57x57.png' },
      { rel: 'apple-touch-icon', sizes: '60x60', href: '/assets/icons/apple-icon-60x60.png' },
      { rel: 'apple-touch-icon', sizes: '72x72', href: '/assets/icons/apple-icon-72x72.png' },
      { rel: 'apple-touch-icon', sizes: '76x76', href: '/assets/icons/apple-icon-76x76.png' },
      { rel: 'apple-touch-icon', sizes: '114x114', href: '/assets/icons/apple-icon-114x114.png' },
      { rel: 'apple-touch-icon', sizes: '120x120', href: '/assets/icons/apple-icon-120x120.png' },
      { rel: 'apple-touch-icon', sizes: '144x144', href: '/assets/icons/apple-icon-144x144.png' },
      { rel: 'apple-touch-icon', sizes: '152x152', href: '/assets/icons/apple-icon-152x152.png' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/assets/icons/apple-icon-180x180.png' },
      { rel: 'icon', type: 'image/png', sizes: '192x192', href: '/assets/icons/android-icon-192x192.png' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/assets/icons/favicon-32x32.png' },
      { rel: 'icon', type: 'image/png', sizes: '96x96', href: '/assets/icons/favicon-96x96.png' },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/assets/icons/favicon-16x16.png' }
    ];

    iconLinks.forEach(({ rel, sizes, href, type }) => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = rel;
        if (sizes) link.sizes = sizes;
        if (type) link.type = type;
        link.href = href;
        document.head.appendChild(link);
      }
    });

  }, []);

  return null;
};

export default SEOHead;
